import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioListField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta03ExemplosEnum,
  ConsultaSeguimentoPergunta03Model,
} from './model-consultaSeguimentoPergunta03'
import { PERGUNTA_03_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta03'
import { calculateResultadoPergunta03, conditionToCalculatePergunta03 } from './utils-consultaSeguimentoPergunta03'

interface ConsultaSeguimentoPergunta03FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta03Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta03Field(props: Readonly<ConsultaSeguimentoPergunta03FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta03Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta03Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta03,
    calculateResultado: calculateResultadoPergunta03,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta03Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_03_SCROLL_CONFIG,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_03_HEADER === SimNaoEnum.SIM

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_03}
            title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' brinca de faz de contas?' />}
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_03_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_03_HEADER')}
              />
            }
          />
        )}
        {!isEmpty(value.PERGUNTA_03_HEADER) && (
          <VFlow id={getId('PERGUNTA_03_EXEMPLOS')}>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              {isPerguntaHeaderSim && (
                <ConsultaSeguimentoIndicatorHelper
                  title='Por favor, me dê um exemplo de uma das brincadeiras de faz de contas do seu filho.'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              )}
            </VFlow>
            <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta03ExemplosEnum>
              name={name.PERGUNTA_03_EXEMPLOS}
              nameOutro={name.OUTRO}
              label='O seu filho alguma vez...'
              description={
                isPerguntaHeaderSim
                  ? 'Responda os exemplos até pontuar “Passou” ou “Falhou”'
                  : 'Pergunte cada um dos exemplos até pontuar “Passou” ou “Falhou”'
              }
              options={PERGUNTA_03_EXEMPLOS_OPTIONS}
              onChange={() => triggerScrollFor('PERGUNTA_03_EXEMPLOS')}
              required
            />
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{ FALHOU: 'Não para todos:', PASSOU: 'Sim para qualquer um:' }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_03_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta03ExemplosEnum>[] = [
  {
    label: 'Fez de conta que bebia em uma xícara de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.BEBIA_XICARA_BRINQUEDO,
  },
  {
    label: 'Fez de conta que comia com uma colher ou um garfo de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.COMIA_COLHER_BRINQUEDO,
  },
  {
    label: 'Fez de conta que falava no telefone?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.FALAVA_TELEFONE,
  },
  {
    label:
      'Fez de conta que dava comida a uma boneca ou a um bichinho de pelúcia, com comida de verdade ou de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.DAVA_COMIDA,
  },
  {
    label: 'Brincou de empurrar um carro de brinquedo, como se o carro andasse em uma estrada?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.EMPURRAR_CARRO,
  },
  {
    label: 'Fez de conta que era um robô, um avião, uma bailarina ou algum dos seus personagens favoritos?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.PERSONAGENS_FAVORITOS,
  },
  {
    label: 'Colocou uma panela de brinquedo em um fogão de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.PANELA_FOGAO,
  },
  {
    label: 'Mexeu uma comida de mentirinha?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.MEXEU_COMIDA,
  },
  {
    label: 'Colocou um boneco dentro de um carro ou um caminhão como se o boneco fosse o motorista ou passageiro?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.MOTORISTA_PASSAGEIRO,
  },
  {
    label: 'Fez de conta que aspirava o tapete, varria o chão ou cortava a grama?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.ASPIRAVA_VARRIA_CORTAVA,
  },
]
